import { toRaw } from 'vue';

// Safe deep clone that handles Vue reactive proxies and non-cloneable values
// structuredClone() cannot clone Vue reactive proxies (throws DataCloneError)
// This function uses toRaw() to unwrap proxies before JSON serialization
export const safeDeepClone = (obj) => {
  if (obj === null || obj === undefined) return obj;
  try {
    // Convert Vue reactive proxy to raw object, then use JSON for deep clone
    const raw = toRaw(obj);
    return JSON.parse(JSON.stringify(raw));
  } catch (e) {
    // Fallback: return shallow copy if JSON fails (e.g., circular refs)
    console.warn('safeDeepClone fallback for object:', e.message);
    return { ...toRaw(obj) };
  }
};

// Helper function to generate a code verifier (random string)
export const generateCodeVerifier = () => {
  const codeVerifier = [...Array(128)].map(() => Math.random().toString(36)[2]).join('');
  return codeVerifier;
};

// Helper function to generate a code challenge from code verifier using SHA-256
export const generateCodeChallenge = async (codeVerifier) => {
  const hashed = await sha256(codeVerifier);
  const base64UrlEncoded = base64URLEncode(hashed);
  return base64UrlEncoded;
};

// Helper function to encode data to base64 URL format
function base64URLEncode(buffer) {
  return btoa(Array.from(new Uint8Array(buffer), (b) => String.fromCharCode(b)).join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Helper function to compute SHA-256 hash
function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
}

export const rgbToRgba = (rgb) => {
  if (!rgb) {
    return;
  }
  const rgbRegex = /^rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)$/i;
  const result = rgb.match(rgbRegex);

  if (!result) {
    return;
  }

  const r = parseInt(result[1], 10);
  const g = parseInt(result[2], 10);
  const b = parseInt(result[3], 10);

  return `rgba(${r}, ${g}, ${b}, 0,0001)`;
};

export const transformText = (inputString) => {
  if (inputString && inputString !== null) {
    const normalizedString = inputString.toString().replace(/[_-]/g, ' ');
    const words = normalizedString.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])|[0-9]+/g);
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    );

    return capitalizedWords.join(' ');
  }
};

export const getAbbreviation = (input) => {
  if (input === null || input === undefined) {
    return;
  }
  return input
    .split('_')
    .map((word) => word[0])
    .join('');
};

export const removeFilterByKey = (filters, key) => {
  const newFilters = { ...filters };

  delete newFilters[key];

  const metric = Object.keys(newFilters).find((metric) => key.startsWith(metric));

  if (metric) {
    const { [key]: removed, ...newMetricFilters } = newFilters[metric] || {};

    if (Object.values(newMetricFilters).some((value) => value !== null)) {
      newFilters[metric] = newMetricFilters;
    } else {
      delete newFilters[metric];
    }
  }

  return newFilters;
};

export const maskEmail = (email) => {
  const [localPart, domain] = email.split('@');
  return localPart.slice(0, 3) + '****' + '@' + domain;
};

export const getQueryParamInviteCode = (param) => {
  // Use decodeURIComponent instead of URLSearchParams.get() to preserve + signs in emails.
  // URLSearchParams.get() decodes + as a space, which corrupts emails like user+1@domain.com.
  const getParam = (search, key) => {
    const escapedKey = encodeURIComponent(key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp('[?&]' + escapedKey + '=([^&]*)');
    const match = search.match(regex);
    return match ? decodeURIComponent(match[1]) : null;
  };

  let value = getParam(window.location.search, param);

  if (!value) {
    const toParam = getParam(window.location.search, 'to');
    if (toParam && toParam.includes('?')) {
      value = getParam('?' + toParam.split('?')[1], param);
    }
  }

  return value;
};

export const copyToClipboard = (text) => {
  return navigator.clipboard.writeText(text);
};

export const arraysAreEqual = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) {
    return false;
  }
  return arr1.every((value, index) => value === arr2[index]);
};

// Функция очистки объекта от ненужных свойств
export const cleanObject = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(cleanObject).filter((item) => item !== null && item !== undefined && item !== 0);
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((acc, key) => {
      if (key === 'valueFormatter' || key === 'visibility' || key === 'cellRenderer') {
        return acc;
      }
      const cleanedValue = cleanObject(obj[key]);
      if (cleanedValue !== null && cleanedValue !== undefined && cleanedValue !== 0) {
        acc[key] = cleanedValue;
      }
      return acc;
    }, {});
  }
  return obj !== null && obj !== undefined && obj !== 0 ? obj : undefined;
};

// Вспомогательная функция для глубокого сравнения объектов
export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (obj1 === null || obj2 === null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return false;
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;

    return obj1.every((item, index) => deepEqual(item, obj2[index]));
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => {
    if (!keys2.includes(key)) return false;
    return deepEqual(obj1[key], obj2[key]);
  });
};

export const makeTransparentInRGB = (color, transparency, background = 'rgb(255, 255, 255)') => {
  const extractColorComponents = (color) => {
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!rgbaMatch) throw new Error(`Invalid color format: ${color}`);

    return {
      r: parseInt(rgbaMatch[1], 10),
      g: parseInt(rgbaMatch[2], 10),
      b: parseInt(rgbaMatch[3], 10),
      a: rgbaMatch[4] !== undefined ? parseFloat(rgbaMatch[4]) : 1,
    };
  };

  try {
    const { r, g, b, a: sourceAlpha } = extractColorComponents(color);
    const { r: br, g: bg, b: bb } = extractColorComponents(background);

    const blend = (c1, c2, alpha) => Math.round((1 - alpha) * c1 + alpha * c2);
    const effectiveAlpha = sourceAlpha * (1 - transparency);

    const resultR = blend(br, r, effectiveAlpha);
    const resultG = blend(bg, g, effectiveAlpha);
    const resultB = blend(bb, b, effectiveAlpha);

    return `rgb(${resultR}, ${resultG}, ${resultB})`;
  } catch (error) {
    console.error('Error processing color:', color, error);
    return 'rgb(255, 255, 255)';
  }
};

export const setDocumentTitle = (title, appName = 'WisePPC', separator = ' | ') => {
  const finalTitle = title ? `${title}${separator}${appName}` : appName;
  document.title = finalTitle;
  return finalTitle;
};

export const toSnakeCase = (str, options = {}) => {
  const { preserveNumbers = true, toLowerCase = true } = options;

  if (!str || typeof str !== 'string') {
    return '';
  }

  // Trim whitespace
  let result = str.trim();

  // Handle empty string after trim
  if (!result) {
    return '';
  }

  // Step 1: Handle different input formats automatically

  // Replace kebab-case and space-separated with underscores
  result = result.replace(/[-\s]+/g, '_');

  // Handle camelCase and PascalCase - insert underscore before uppercase letters
  // but not at the beginning and not after another underscore
  result = result.replace(/([a-z0-9])([A-Z])/g, '$1_$2');

  // Handle sequences of uppercase letters (like XMLHttpRequest -> XML_Http_Request)
  result = result.replace(/([A-Z])([A-Z][a-z])/g, '$1_$2');

  // Handle numbers if preserveNumbers is true
  if (preserveNumbers) {
    // Insert underscore before numbers that follow letters
    result = result.replace(/([a-zA-Z])([0-9])/g, '$1_$2');
    // Insert underscore after numbers that are followed by letters
    result = result.replace(/([0-9])([a-zA-Z])/g, '$1_$2');
  }

  // Step 2: Clean up the result

  // Replace multiple consecutive underscores with single underscore
  result = result.replace(/_+/g, '_');

  // Remove leading and trailing underscores
  result = result.replace(/^_+|_+$/g, '');

  // Convert to lowercase if requested
  if (toLowerCase) {
    result = result.toLowerCase();
  }

  return result;
};
